import { observer } from 'mobx-react-lite'
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native'

import { Player } from '@ppwr/poker'

import { useStore } from '../../../models'
import { PlayerBettingChips } from '../PlayerBettingChips'
import { getLastActionText } from '../utils'
import { PlayerCellContent } from './PlayerCellContent'
import { PlayerCellStatus } from './PlayerCellStatus'
import { useTableTourPositioning } from '../../../hooks/usePositioning'
import React, { useEffect, useState } from 'react'
import { usePlayerControl } from '../TeacherControl/TeacherControl'
import { LayoutShiftAnchor } from '@/hooks/useLayoutShiftRegistry'

export interface PlayerCellProps {
  player: Player
  onPlayerCellPress?: (player: Player) => void
  right?: boolean
  numberOfPlayers?: number
  savePositionReference?: boolean
  bottom?: boolean
}

export const PlayerCell = observer(
  ({ player, onPlayerCellPress, right, savePositionReference = true, bottom }: PlayerCellProps) => {
    const { selectedPlayer } = usePlayerControl()
    const {
      game: { isCardsVisibleOnShowDown, isWalkthrough, walkthroughType, currentStep, stageInternalName, step }
    } = useStore()

    const { ref, onLayout } = useTableTourPositioning({
      key: player.role as 'D' | 'SB' | 'BB',
      shouldSave: () => savePositionReference && Boolean(player.role)
    })
    const [height, setHeight] = useState(0)
    const [width, setWidth] = useState(0)

    useEffect(() => {
      if (Platform.OS === 'web' && step > 0 && walkthroughType) {
        onLayout()
        //HACK: the measure is done once on web, not after the layout changes, we need to retrigger to show the player cell at correct position within table tour
      }
    }, [step, walkthroughType, onLayout])

    const lastAction = !isWalkthrough ? getLastActionText(player) : undefined

    const showIndicator = walkthroughType === 'RaiseOrFold' && currentStep?.id === player.id && step > 0

    const enablePressPlayer = !(isCardsVisibleOnShowDown || isWalkthrough) && onPlayerCellPress

    return (
      <LayoutShiftAnchor name={`myFirstGame.playerCell-${player.name}`}>
        <View
          ref={ref}
          onLayout={(event) => {
            onLayout()
            setHeight(event.nativeEvent.layout.height)
            setWidth(event.nativeEvent.layout.width)
          }}
          style={[
            styles.container,
            right && styles.rightContainer,
            bottom && styles.bottomContainer,
            {
              zIndex: 9999
            }
          ]}
          collapsable={false}
        >
          {bottom && <PlayerBettingChips player={player} horizontal={true} bottom />}
          <TouchableOpacity
            style={[
              {
                ...styles.playerCellContainer
              },
              {
                opacity: selectedPlayer && selectedPlayer.userId !== player.userId ? 0.2 : 1
              },
              bottom && styles.bottomCellContainer
            ]}
            onPress={() => enablePressPlayer && onPlayerCellPress?.(player)}
            activeOpacity={onPlayerCellPress ? 0.2 : 1}
          >
            <PlayerCellContent player={player} right={right} bottom={bottom} showIndicator={showIndicator} />
            <>
              {lastAction && (
                <View
                  style={[
                    { alignSelf: right ? 'flex-end' : 'flex-start', maxWidth: 100 },
                    bottom && styles.bottomCellContainer
                  ]}
                >
                  <PlayerCellStatus player={player} right={right} lastAction={lastAction} />
                </View>
              )}
              <View
                style={{
                  alignSelf: right ? 'flex-end' : bottom ? 'center' : 'flex-start',
                  width: '100%',
                  maxWidth: 100
                }}
              >
                <PlayerCellStatus bottom={bottom} player={player} right={right} />
              </View>
            </>
          </TouchableOpacity>
          {!bottom && <PlayerBettingChips player={player} />}
        </View>
      </LayoutShiftAnchor>
    )
  }
)

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5
  },
  rightContainer: {
    flexDirection: 'row-reverse'
  },
  bottomContainer: {
    flexDirection: 'column',
    gap: 8
    //marginTop: -10
  },
  bottomContainerWithChips: {
    flexDirection: 'column',
    gap: 8,
    marginTop: -35
  },
  playerCellContainer: {
    justifyContent: 'flex-start',
    width: '100%',
    gap: 5
  },
  bottomCellContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    width: '100%'
  }
})
